/**
 * Voice Generation Service (Qwen3-TTS)
 * Supports local ComfyUI and cloud Replicate inference
 *
 * Models used:
 * - Qwen3-TTS-12Hz-1.7B-CustomVoice (preset speakers with style instructions)
 * - Qwen3-TTS-12Hz-1.7B-VoiceDesign (create voice from description)
 * - Qwen3-TTS-12Hz-1.7B-Base (voice cloning from reference audio)
 */

import { VoiceGenerationRequest, VoiceGenerationResult, VoiceMode, QwenPresetSpeaker, QwenTTSLanguage } from '../types';
import { prepareVRAMForTask } from './comfyui-vram';

// ============================================
// LOCAL INFERENCE TOGGLE
// ============================================
const USE_LOCAL_INFERENCE = import.meta.env.VITE_USE_LOCAL_INFERENCE === 'true';
const LOCAL_COMFYUI_URL = import.meta.env.VITE_LOCAL_COMFYUI_URL || 'http://127.0.0.1:8188';

// Only run ComfyUI checks on localhost (prevents CORS errors in production)
const isLocalhost = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

if (USE_LOCAL_INFERENCE && isLocalhost) {
    console.log('🏠 VOICE: LOCAL INFERENCE MODE - Using ComfyUI at', LOCAL_COMFYUI_URL);
    // Check available nodes on startup
    setTimeout(async () => {
        try {
            const response = await fetch(`${LOCAL_COMFYUI_URL}/object_info`);
            if (response.ok) {
                const objectInfo = await response.json();
                const allNodes = Object.keys(objectInfo);
                const qwenNodes = allNodes.filter(name =>
                    name.toLowerCase().includes('qwen') ||
                    name.toLowerCase().includes('tts')
                );
                console.log('🔍 Available Qwen/TTS nodes:', qwenNodes);
            }
        } catch (e) {
            // Silently ignore - ComfyUI not available
        }
    }, 1000);
}

// ============================================
// SUPABASE EDGE FUNCTION URL
// ============================================
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// ============================================
// COMFYUI WORKFLOW TEMPLATES
// ============================================

// Node class types - these are the actual registered class names in ComfyUI
// Found via /object_info endpoint: FB_Qwen3TTSCustomVoice, FB_Qwen3TTSVoiceDesign, FB_Qwen3TTSVoiceClone
const NODE_CLASS_TYPES = {
    customVoice: 'FB_Qwen3TTSCustomVoice',   // Preset speaker voices with optional style
    voiceDesign: 'FB_Qwen3TTSVoiceDesign',   // Design voice from description
    voiceClone: 'FB_Qwen3TTSVoiceClone',     // Clone voice from reference audio
    loadAudio: 'LoadAudio',
    saveAudio: 'SaveAudio'
};

// ComfyUI expects speaker names in specific format (lowercase after underscore)
const COMFYUI_SPEAKER_MAP: Record<string, string> = {
    'Vivian': 'Vivian',
    'Serena': 'Serena',
    'Uncle_Fu': 'Uncle_fu',
    'Dylan': 'Dylan',
    'Eric': 'Eric',
    'Ryan': 'Ryan',
    'Aiden': 'Aiden',
    'Ono_Anna': 'Ono_anna',
    'Sohee': 'Sohee'
};

/**
 * Build ComfyUI workflow for Custom Voice generation
 * Uses preset speakers with optional style instructions
 */
function buildCustomVoiceWorkflow(request: VoiceGenerationRequest): object {
    const speaker = request.speaker || "Serena";
    const comfyuiSpeaker = COMFYUI_SPEAKER_MAP[speaker] || speaker;

    return {
        "1": {
            "inputs": {
                "text": request.text,
                "speaker": comfyuiSpeaker,
                "instruct": request.style_instruction || "",
                "language": request.language || "Auto",
                "model_choice": "1.7B",
                "device": "auto",
                "precision": "bf16",
                "seed": Math.floor(Math.random() * 1000000000),
                "max_new_tokens": 2048,
                "top_p": 0.80,
                "top_k": 20,
                "temperature": 1.0,
                "repetition_penalty": 1.05,
                "attention": "sdpa",
                "unload_model_after_generate": false
            },
            "class_type": NODE_CLASS_TYPES.customVoice
        },
        "2": {
            "inputs": {
                "audio": ["1", 0],
                "filename_prefix": "ezart_voice"
            },
            "class_type": NODE_CLASS_TYPES.saveAudio
        }
    };
}

/**
 * Build ComfyUI workflow for Voice Design generation
 * Creates a unique voice from natural language description
 */
function buildVoiceDesignWorkflow(request: VoiceGenerationRequest): object {
    return {
        "1": {
            "inputs": {
                "text": request.text,
                "instruct": request.voice_description || "A clear, natural speaking voice",
                "language": request.language || "Auto",
                "model_choice": "1.7B",
                "device": "auto",
                "precision": "bf16",
                "seed": Math.floor(Math.random() * 1000000000),
                "max_new_tokens": 2048,
                "top_p": 0.80,
                "top_k": 20,
                "temperature": 1.0,
                "repetition_penalty": 1.05,
                "attention": "sdpa",
                "unload_model_after_generate": false
            },
            "class_type": NODE_CLASS_TYPES.voiceDesign
        },
        "2": {
            "inputs": {
                "audio": ["1", 0],
                "filename_prefix": "ezart_voice_design"
            },
            "class_type": NODE_CLASS_TYPES.saveAudio
        }
    };
}

/**
 * Build ComfyUI workflow for Voice Clone generation
 * Requires reference audio to be uploaded first (5-15 seconds recommended)
 */
function buildVoiceCloneWorkflow(request: VoiceGenerationRequest, uploadedAudioName: string): object {
    // Build base inputs for voice clone
    const cloneInputs: Record<string, any> = {
        "ref_audio": ["1", 0],
        "ref_text": request.reference_text || "",
        "target_text": request.text,
        "language": request.language || "Auto",
        "model_choice": "1.7B",
        "device": "auto",
        "precision": "bf16",
        "seed": Math.floor(Math.random() * 1000000000),
        "max_new_tokens": 2048,
        "top_p": 0.80,
        "top_k": 20,
        "temperature": 1.0,
        "repetition_penalty": 1.05,
        "attention": "sdpa",
        "unload_model_after_generate": false
    };

    // Add style instruction if provided (vocal direction)
    if (request.style_instruction) {
        cloneInputs["instruct"] = request.style_instruction;
    }

    // Include subfolder prefix since we upload to 'audio' subfolder
    const audioPath = uploadedAudioName.includes('/') ? uploadedAudioName : `audio/${uploadedAudioName}`;

    return {
        "1": {
            "inputs": {
                "audio": audioPath
            },
            "class_type": NODE_CLASS_TYPES.loadAudio
        },
        "2": {
            "inputs": cloneInputs,
            "class_type": NODE_CLASS_TYPES.voiceClone
        },
        "3": {
            "inputs": {
                "audio": ["2", 0],
                "filename_prefix": "ezart_voice_clone"
            },
            "class_type": NODE_CLASS_TYPES.saveAudio
        }
    };
}

// ============================================
// LOCAL COMFYUI INFERENCE
// ============================================

/**
 * Upload audio to ComfyUI for voice cloning
 */
async function uploadAudioToComfyUI(audioData: string): Promise<string> {
    // audioData can be base64 or URL
    let blob: Blob;

    if (audioData.startsWith('data:')) {
        // Base64 data URL - handle formats like:
        // data:audio/webm;base64,...
        // data:audio/webm;codecs=opus;base64,...
        const match = audioData.match(/^data:([^;,]+)(?:;[^;,]*)*;base64,(.+)$/);
        if (match) {
            const mimeType = match[1];
            const base64 = match[2];
            const binaryString = atob(base64);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            blob = new Blob([bytes], { type: mimeType });
        } else {
            console.error('Failed to parse data URL:', audioData.substring(0, 100));
            throw new Error('Invalid base64 data URL');
        }
    } else if (audioData.startsWith('http')) {
        // Fetch from URL
        const response = await fetch(audioData);
        blob = await response.blob();
    } else {
        throw new Error('Invalid audio data format');
    }

    // Upload to ComfyUI
    const formData = new FormData();
    formData.append('image', blob, `ref_audio_${Date.now()}.wav`);
    formData.append('type', 'input');
    formData.append('subfolder', 'audio');

    const response = await fetch(`${LOCAL_COMFYUI_URL}/upload/image`, {
        method: 'POST',
        body: formData
    });

    if (!response.ok) {
        throw new Error(`Failed to upload audio to ComfyUI: ${response.statusText}`);
    }

    const result = await response.json();
    return result.name;
}

/**
 * Generate voice using local ComfyUI
 */
async function generateVoiceWithLocalComfyUI(request: VoiceGenerationRequest): Promise<VoiceGenerationResult> {
    console.log('🏠 Generating voice with local ComfyUI...');

    let workflow: object;

    // Build workflow based on mode
    switch (request.voice_mode) {
        case 'custom_voice':
            workflow = buildCustomVoiceWorkflow(request);
            break;
        case 'voice_design':
            workflow = buildVoiceDesignWorkflow(request);
            break;
        case 'voice_clone':
            if (!request.reference_audio) {
                throw new Error('Reference audio is required for voice cloning');
            }
            // Upload reference audio first
            const uploadedName = await uploadAudioToComfyUI(request.reference_audio);
            workflow = buildVoiceCloneWorkflow(request, uploadedName);
            break;
        default:
            throw new Error(`Unknown voice mode: ${request.voice_mode}`);
    }

    // Log the workflow for debugging
    console.log('📤 Sending workflow to ComfyUI:', JSON.stringify(workflow, null, 2));

    // Free VRAM if switching from image generation to TTS
    await prepareVRAMForTask('tts');

    // Queue the prompt
    const queueResponse = await fetch(`${LOCAL_COMFYUI_URL}/prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: workflow })
    });

    if (!queueResponse.ok) {
        const errorText = await queueResponse.text();
        console.error('❌ ComfyUI prompt error:', errorText);
        throw new Error(`Failed to queue ComfyUI prompt: ${queueResponse.status} - ${errorText}`);
    }

    const queueResult = await queueResponse.json();
    const promptId = queueResult.prompt_id;
    console.log(`📋 Queued ComfyUI prompt: ${promptId}`);

    // Poll for completion
    const maxPolls = 300; // 5 minutes max
    let polls = 0;

    while (polls < maxPolls) {
        await new Promise(r => setTimeout(r, 1000));
        polls++;

        const historyResponse = await fetch(`${LOCAL_COMFYUI_URL}/history/${promptId}`);
        if (!historyResponse.ok) continue;

        const history = await historyResponse.json();
        const promptHistory = history[promptId];

        if (promptHistory && promptHistory.outputs) {
            // Find audio output
            for (const nodeId of Object.keys(promptHistory.outputs)) {
                const output = promptHistory.outputs[nodeId];
                if (output.audio && output.audio.length > 0) {
                    const audioInfo = output.audio[0];
                    const audioUrl = `${LOCAL_COMFYUI_URL}/view?filename=${encodeURIComponent(audioInfo.filename)}&subfolder=${encodeURIComponent(audioInfo.subfolder || '')}&type=${audioInfo.type || 'output'}`;

                    console.log('✅ Voice generated locally:', audioUrl);

                    // Fetch the audio blob
                    const audioResponse = await fetch(audioUrl);
                    const audioBlob = await audioResponse.blob();

                    return {
                        audioBlob,
                        audioUrl,
                        model: `qwen3-tts-1.7b-${request.voice_mode}`
                    };
                }
            }
        }

        // Check for errors
        if (promptHistory && promptHistory.status?.status_str === 'error') {
            throw new Error(`ComfyUI error: ${promptHistory.status?.messages?.[0] || 'Unknown error'}`);
        }
    }

    throw new Error('Timeout waiting for ComfyUI voice generation');
}

// ============================================
// CLOUD REPLICATE INFERENCE
// ============================================

/**
 * Generate voice using Replicate API via Supabase Edge Function
 */
async function generateVoiceWithReplicate(request: VoiceGenerationRequest): Promise<VoiceGenerationResult> {
    console.log('☁️ Generating voice with Replicate...');

    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
        throw new Error('Supabase configuration missing');
    }

    // Replicate expects lowercase "auto" but other languages are capitalized
    const replicateLanguage = (request.language === 'Auto' || !request.language)
        ? 'auto'
        : request.language;

    const body: Record<string, any> = {
        text: request.text,
        mode: request.voice_mode,
        language: replicateLanguage,
    };

    // Add mode-specific parameters
    if (request.voice_mode === 'custom_voice' && request.speaker) {
        body.speaker = request.speaker;
    }

    if (request.voice_mode === 'voice_clone') {
        body.reference_audio = request.reference_audio;
        if (request.reference_text) {
            body.reference_text = request.reference_text;
        }
    }

    if (request.voice_mode === 'voice_design' && request.voice_description) {
        body.voice_description = request.voice_description;
    }

    if (request.style_instruction) {
        body.style_instruction = request.style_instruction;
    }

    // Call Edge Function
    const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-voice`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(`Voice generation failed: ${errorData.error || response.statusText}`);
    }

    const result = await response.json();
    console.log('✅ Voice generated via Replicate');

    // The output should be an audio URL from Replicate
    const audioUrl = typeof result.output === 'string' ? result.output : result.output?.[0];

    if (!audioUrl) {
        throw new Error('No audio URL in response');
    }

    return {
        audioUrl,
        model: 'qwen/qwen3-tts'
    };
}

// ============================================
// MAIN GENERATION FUNCTION
// ============================================

/**
 * Generate voice audio from text
 * Automatically routes to local ComfyUI or cloud Replicate based on settings
 */
export async function generateVoice(request: VoiceGenerationRequest): Promise<VoiceGenerationResult> {
    console.log(`🎤 Voice generation request:`, {
        text: request.text.substring(0, 50) + '...',
        mode: request.voice_mode,
        language: request.language,
        useLocal: USE_LOCAL_INFERENCE
    });

    try {
        if (USE_LOCAL_INFERENCE) {
            return await generateVoiceWithLocalComfyUI(request);
        }
        return await generateVoiceWithReplicate(request);
    } catch (error) {
        console.error('❌ Voice generation error:', error);

        // If local fails, try cloud as fallback
        if (USE_LOCAL_INFERENCE) {
            console.log('🔄 Falling back to Replicate...');
            try {
                return await generateVoiceWithReplicate(request);
            } catch (fallbackError) {
                console.error('❌ Fallback also failed:', fallbackError);
                throw error; // Throw original error
            }
        }

        throw error;
    }
}

/**
 * Generate a voice preview for testing voice configuration
 */
export async function generateVoicePreview(
    voiceMode: VoiceMode,
    options: {
        speaker?: QwenPresetSpeaker;
        voiceDescription?: string;
        referenceAudio?: string;
        referenceText?: string;
        language?: QwenTTSLanguage;
        styleInstruction?: string;
        previewText?: string;
        characterName?: string;
    }
): Promise<VoiceGenerationResult> {
    const name = options.characterName || '';

    // Default sample texts based on language (used if no custom text provided)
    // Includes character name when available
    const sampleTexts: Record<string, string> = {
        'Auto': name ? `Hello, my name is ${name}. It's nice to meet you!` : 'Hello, this is a voice preview.',
        'Chinese': name ? `你好，我叫${name}。很高兴认识你！` : '你好，这是一个语音预览。',
        'English': name ? `Hello, my name is ${name}. It's nice to meet you!` : 'Hello, this is a voice preview.',
        'Japanese': name ? `こんにちは、私は${name}です。お会いできて嬉しいです！` : 'こんにちは、これは音声プレビューです。',
        'Korean': name ? `안녕하세요, 저는 ${name}입니다. 만나서 반갑습니다!` : '안녕하세요, 이것은 음성 미리보기입니다.',
        'German': name ? `Hallo, ich heiße ${name}. Freut mich, Sie kennenzulernen!` : 'Hallo, dies ist eine Sprachvorschau.',
        'French': name ? `Bonjour, je m'appelle ${name}. Enchanté!` : 'Bonjour, ceci est un aperçu vocal.',
        'Russian': name ? `Привет, меня зовут ${name}. Приятно познакомиться!` : 'Привет, это предварительный просмотр голоса.',
        'Portuguese': name ? `Olá, meu nome é ${name}. Prazer em conhecê-lo!` : 'Olá, esta é uma prévia de voz.',
        'Spanish': name ? `Hola, me llamo ${name}. ¡Encantado de conocerte!` : 'Hola, esta es una vista previa de voz.',
        'Italian': name ? `Ciao, mi chiamo ${name}. Piacere di conoscerti!` : 'Ciao, questa è un\'anteprima vocale.',
    };

    const language = options.language || 'Auto';
    // Use custom preview text if provided, otherwise fall back to language-based sample
    const text = options.previewText?.trim() || sampleTexts[language] || sampleTexts['English'];

    const request: VoiceGenerationRequest = {
        text,
        voice_mode: voiceMode,
        language,
        speaker: options.speaker,
        voice_description: options.voiceDescription,
        reference_audio: options.referenceAudio,
        reference_text: options.referenceText,
        style_instruction: options.styleInstruction,
    };

    return generateVoice(request);
}

/**
 * Check if local ComfyUI is available for voice generation
 * Also logs available Qwen/TTS node names for debugging
 */
export async function checkLocalVoiceAvailability(): Promise<{ available: boolean; nodes: string[] }> {
    if (!USE_LOCAL_INFERENCE) return { available: false, nodes: [] };

    try {
        const response = await fetch(`${LOCAL_COMFYUI_URL}/object_info`, {
            method: 'GET',
            signal: AbortSignal.timeout(5000)
        });

        if (!response.ok) return { available: false, nodes: [] };

        const objectInfo = await response.json();
        const allNodes = Object.keys(objectInfo);

        // Find all Qwen/TTS related nodes (case insensitive search)
        const qwenNodes = allNodes.filter(name =>
            name.toLowerCase().includes('qwen') ||
            name.toLowerCase().includes('tts')
        );

        console.log('🔍 Found Qwen/TTS nodes in ComfyUI:', qwenNodes);

        // Check for the specific nodes we need (try multiple naming conventions)
        const customVoiceNames = ['Qwen3TTSCustomVoice', 'Qwen3-TTS CustomVoice', 'QwenTTSCustomVoiceNode'];
        const voiceDesignNames = ['Qwen3TTSVoiceDesign', 'Qwen3-TTS VoiceDesign', 'QwenTTSVoiceDesignNode'];
        const voiceCloneNames = ['Qwen3TTSVoiceClone', 'Qwen3-TTS VoiceClone', 'QwenTTSVoiceCloneNode'];

        const hasCustomVoice = customVoiceNames.some(n => allNodes.includes(n));
        const hasVoiceDesign = voiceDesignNames.some(n => allNodes.includes(n));
        const hasVoiceClone = voiceCloneNames.some(n => allNodes.includes(n));

        console.log('🎤 ComfyUI Qwen3-TTS nodes check:', {
            hasCustomVoice,
            hasVoiceDesign,
            hasVoiceClone,
            allQwenNodes: qwenNodes
        });

        return {
            available: hasCustomVoice || hasVoiceDesign || hasVoiceClone || qwenNodes.length > 0,
            nodes: qwenNodes
        };
    } catch (error) {
        console.warn('⚠️ Cannot check local ComfyUI availability:', error);
        return { available: false, nodes: [] };
    }
}

// Export for convenience
export { USE_LOCAL_INFERENCE };
